// src/utils/device.js

import FingerprintJS from '@fingerprintjs/fingerprintjs'

const LS_KEY = 'DEVICE_PROFILE_V1'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24h

let fpPromise = null
let cached = null
let cachedPromise = null

function nowTs() {
  return Date.now()
}

function safe(val, def = '') {
  return val === undefined || val === null ? def : val
}

function getTimezone() {
  try {
    return safe(Intl.DateTimeFormat().resolvedOptions().timeZone, '')
  } catch (e) {
    // ignore
    return ''
  }
}

function getScreenStr() {
  try {
    const w = safe(window.screen && window.screen.width, '')
    const h = safe(window.screen && window.screen.height, '')
    return w && h ? `${w}x${h}` : ''
  } catch (e) {
    // ignore
    return ''
  }
}

function getPlatform() {
  try {
    return safe(navigator.platform, '')
  } catch (e) {
    // ignore
    return ''
  }
}

function getCpuCores() {
  try {
    return safe(navigator.hardwareConcurrency, '')
  } catch (e) {
    // ignore
    return ''
  }
}

function getDeviceMemory() {
  try {
    return safe(navigator.deviceMemory, '')
  } catch (e) {
    // ignore
    return ''
  }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.id) return null

    const ts = Number(parsed.ts || 0)
    if (ts && nowTs() - ts > CACHE_TTL_MS) return null

    return parsed
  } catch (e) {
    // ignore
    return null
  }
}

function saveToStorage(profile) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(profile))
  } catch (e) {
    // ignore
  }
}

function initFP() {
  if (!fpPromise) fpPromise = FingerprintJS.load()
  return fpPromise
}

function fallbackId() {
  return `fp_${Math.random().toString(36).slice(2)}_${nowTs().toString(36)}`
}

async function buildProfile() {
  const base = {
    id: '',
    ts: nowTs(),
    tz: getTimezone(),
    platform: getPlatform(),
    screen: getScreenStr(),
    cpu: getCpuCores(),
    mem: getDeviceMemory()
  }

  try {
    const fp = await initFP()
    const res = await fp.get()
    const vid = safe(res && res.visitorId, '')
    const profile = {
      ...base,
      id: vid || fallbackId()
    }
    return profile
  } catch (e) {
    return {
      ...base,
      id: fallbackId()
    }
  }
}

/**
 * 获取设备画像（带缓存 + 并发去重）
 * 返回：{ id, ts, tz, platform, screen, cpu, mem }
 */
export function getDeviceProfile() {
  if (cached) return Promise.resolve(cached)
  const fromLS = loadFromStorage()
  if (fromLS) {
    cached = fromLS
    return Promise.resolve(fromLS)
  }

  if (cachedPromise) return cachedPromise

  cachedPromise = buildProfile()
    .then((profile) => {
      cached = profile
      saveToStorage(profile)
      cachedPromise = null
      return profile
    })
    .catch((err) => {
      cachedPromise = null
      throw err
    })

  return cachedPromise
}

/**
 * 手动清理缓存（比如退出登录时）
 */
export function clearDeviceProfile() {
  cached = null
  cachedPromise = null
  try {
    localStorage.removeItem(LS_KEY)
  } catch (e) {
    // ignore
  }
}
